import { fromBlob } from 'image-resize-compress';
import { forwardRef, useState } from 'react';
import { toast } from 'react-toastify';
import ReactLoading from 'react-loading';
import useSectionHighlightRef from '../../../../hooks/useSectionHighlightRef';

const ImageUpOrg = forwardRef(({ imgData, setImgData, isEdit }, ref) => {
    const { sectionRef, highlightField } = useSectionHighlightRef(ref);
    const [draggedItem, setDraggedItem] = useState(null);
    const [loadingWallImgCount, setLoadingWallImgCount] = useState(0);
    const [loadingThumbImgCount, setLoadingThumbImgCount] = useState(0);
    const delay = (ms) => new Promise((res) => setTimeout(res, ms));

    async function handelWallImage(e) {
        if (!e.target.files[0]) {
            setImgData((prevState) => ({ ...prevState, newWallImg: '' }));
            return;
        }

        const selectedFiles = Array.from(e.target.files);
        const sizeLimit = 2 * 1024 * 1024;

        if (selectedFiles.length > 20) {
            toast.error('You can select only up to 20 images');
            return;
        }

        setLoadingWallImgCount(selectedFiles.length);

        try {
            const compressedPromises = selectedFiles.map(async (file) => {
                await delay(2000);
                if (file.size <= sizeLimit) {
                    return file;
                }

                const quality = 70;
                const width = 'auto';
                const height = 'auto';

                const compressed = await fromBlob(file, quality, width, height);

                const compressedFile = new File([compressed], file.name, {
                    type: compressed.type,
                    lastModified: file.lastModified
                });

                return compressedFile;
            });

            const compressedFiles = await Promise.all(compressedPromises);

            setImgData((prevState) => ({
                ...prevState,
                newWallImg: compressedFiles
            }));
        } catch (error) {
            console.error('Error compressing images:', error);
            toast.error('Image compression failed. Please try again.');
        } finally {
            setLoadingWallImgCount(0);
        }
    }

    async function handelThumbImage(e) {
        if (!e.target.files[0]) {
            setImgData((prevState) => ({ ...prevState, newThumbImg: '' }));
            return;
        }

        const selectedFiles = Array.from(e.target.files);
        const sizeLimit = 500 * 1024;

        if (selectedFiles.length > 5) {
            toast.error('You can select only up to 5 images');
            return;
        }

        setLoadingThumbImgCount(selectedFiles.length);

        try {
            const compressedPromises = selectedFiles.map(async (file) => {
                if (file.size <= sizeLimit) return file;

                const quality = 20;
                const width = 'auto';
                const height = 'auto';

                const compressed = await fromBlob(file, quality, width, height);

                const compressedFile = new File([compressed], file.name, {
                    type: compressed.type,
                    lastModified: file.lastModified
                });

                return compressedFile;
            });

            const compressedFiles = await Promise.all(compressedPromises);

            setImgData((prevState) => ({
                ...prevState,
                newThumbImg: compressedFiles
            }));
        } catch (error) {
            console.error('Error compressing thumbnails:', error);
            toast.error('Thumbnail compression failed. Please try again.');
        } finally {
            setLoadingThumbImgCount(0);
        }
    }

    const handleDragStart = (index, type) => {
        setDraggedItem({ index, type });
    };

    const handleDragOver = (e) => {
        e.preventDefault(); // Necessary to allow dropping
    };

    const handleDrop = (dropIndex, type) => {
        if (!draggedItem || draggedItem.type !== type || draggedItem.index === dropIndex) return;

        const imageKey = type === 'wall' ? 'newWallImg' : 'newThumbImg';
        const fallbackKey = type === 'wall' ? 'oldWallImg' : 'oldThumbImg';

        const currentImages = imgData?.[imageKey]?.length > 0 ? [...imgData[imageKey]] : [...imgData[fallbackKey]];
        const [movedImg] = currentImages.splice(draggedItem.index, 1);
        currentImages.splice(dropIndex, 0, movedImg);

        setImgData((prev) => ({
            ...prev,
            [imgData?.[imageKey]?.length > 0 ? imageKey : fallbackKey]: currentImages
        }));

        setDraggedItem(null);
    };

    return (
        <div ref={sectionRef}>
            <h2 className='orgPackUpH2'>Images</h2>
            {/* image upload div */}
            <div className='orgPackUpSecDiv'>
                {isEdit && (
                    <div>
                        <p className='tagsNoteP'>Note: Uploading new image will replace all the old images</p>
                    </div>
                )}
                <div className='itineraryInDiv'>
                    <label className='itineraryLabel'>Wallpaper Images (upto 20)</label>
                    <div className='itineraryInputDiv'>
                        <input
                            type='file'
                            multiple
                            accept='.jpg, .jpeg, .png, .webp'
                            className='itineraryInput'
                            onChange={(e) => handelWallImage(e)}
                        />
                    </div>
                </div>
                {loadingWallImgCount > 0 ? (
                    <>
                        <p className='dragNote'>Note: Use drag and drop to reorder the Wallpaper images</p>
                        <div className='imgDisplayDiv'>
                            {Array.from({ length: loadingWallImgCount }).map((_, i) => (
                                <div className='imageSpinnerContainer' key={i}>
                                    <ReactLoading type='spin' color='#006aff' height={30} width={30} />
                                </div>
                            ))}
                        </div>
                    </>
                ) : imgData?.newWallImg?.length !== 0 || imgData?.oldWallImg?.length !== 0 ? (
                    <>
                        <p className='dragNote'>Note: Use drag and drop to reorder the Wallpaper images</p>
                        <div className='imgDisplayDiv'>
                            {(() => {
                                let wallImg = imgData?.newWallImg?.length > 0 ? imgData.newWallImg : imgData.oldWallImg;

                                return wallImg?.map((upImg, imgKey) => {
                                    // To display the image convert it to URL
                                    const fileURL = typeof upImg === 'string' ? upImg : URL.createObjectURL(upImg);

                                    return (
                                        <img
                                            src={fileURL}
                                            alt='trvto_package'
                                            key={imgKey}
                                            className='wallImgDisp'
                                            loading='lazy'
                                            decoding='async'
                                            draggable
                                            onDragStart={() => handleDragStart(imgKey, 'wall')}
                                            onDragOver={handleDragOver}
                                            onDrop={() => handleDrop(imgKey, 'wall')}
                                        />
                                    );
                                });
                            })()}
                        </div>
                    </>
                ) : null}
                <div className='itineraryInDiv'>
                    {/* small image upload div */}
                    <label className='itineraryLabel'>Thumbnail Images (upto 6)</label>
                    <div className='itineraryInputDiv'>
                        <input
                            type='file'
                            multiple
                            accept='.jpg, .jpeg, .png, .webp'
                            className='itineraryInput'
                            onChange={(e) => handelThumbImage(e)}
                        />
                    </div>
                </div>
                {loadingThumbImgCount > 0 ? (
                    <>
                        <p className='dragNote'>Note: Use drag and drop to reorder the Wallpaper images</p>
                        <div className='imgDisplayDiv'>
                            {Array.from({ length: loadingThumbImgCount }).map((_, i) => (
                                <div className='imageSpinnerContainer' key={i}>
                                    <ReactLoading type='spin' color='#006aff' height={30} width={30} />
                                </div>
                            ))}
                        </div>
                    </>
                ) : (imgData?.newThumbImg?.length !== 0 || imgData?.oldThumbImg?.length) !== 0 ? (
                    <>
                        <p className='dragNote'>Note: Use drag and drop to reorder the Thumbnail images</p>
                        <div className='imgDisplayDiv'>
                            {(() => {
                                let thumbImg =
                                    imgData?.newThumbImg?.length > 0 ? imgData.newThumbImg : imgData.oldThumbImg;

                                return thumbImg?.map((upImg, imgKey) => {
                                    // To display the image convert it to URL
                                    const fileURL = typeof upImg === 'string' ? upImg : URL.createObjectURL(upImg);

                                    return (
                                        <img
                                            src={fileURL}
                                            alt='trvto_package'
                                            key={imgKey}
                                            className='wallImgDisp'
                                            loading='lazy'
                                            decoding='async'
                                            draggable
                                            onDragStart={() => handleDragStart(imgKey, 'thumb')}
                                            onDragOver={handleDragOver}
                                            onDrop={() => handleDrop(imgKey, 'thumb')}
                                        />
                                    );
                                });
                            })()}
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
});

export default ImageUpOrg;
